/**
 * SchoolOnboarding.jsx
 *
 * Post-signup onboarding wizard. Shown immediately after school registration.
 * Three steps:
 *   1. Welcome — confirm school is live, show slug-based URLs
 *   2. Plan selection — free vs paid (Starter/Growth), trigger Stripe checkout
 *   3. Done — "Open your dashboard"
 *
 * Props:
 *   schoolSlug      string   e.g. "university-of-helsinki"
 *   schoolName      string   e.g. "University of Helsinki"
 *   adminName       string
 *   onEnterDashboard fn      called when user clicks "Open dashboard"
 */

import { useState } from "react";
import { useAuth } from "../lib/AuthContext";
import { COLORS } from "../lib/colors";

const API = import.meta.env.VITE_API_URL || "https://relay-backend-app.fly.dev";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "€0",
    period: "forever",
    highlight: false,
    features: [
      "Up to 10 active recruiters",
      "Unlimited student uploads",
      "Consent-driven CV sharing",
      "Recruiter approval workflow",
    ],
    cta: "Continue on Free",
    ctaStyle: "secondary",
  },
  {
    id: "starter",
    name: "Starter",
    price: "€299",
    period: "per year",
    highlight: true,
    badge: "Most popular",
    features: [
      "Up to 50 active recruiters",
      "Everything in Free",
      "Engagement reports",
      "Priority email support",
    ],
    cta: "Upgrade to Starter",
    ctaStyle: "primary",
  },
  {
    id: "growth",
    name: "Growth",
    price: "€699",
    period: "per year",
    highlight: false,
    features: [
      "Unlimited recruiters",
      "Everything in Starter",
      "Advanced analytics",
      "White-label options",
    ],
    cta: "Upgrade to Growth",
    ctaStyle: "secondary",
  },
];

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        backgroundColor: COLORS.surfaceAlt,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8, padding: "8px 12px",
      }}>
        <span style={{ flex: 1, fontSize: 13, color: COLORS.text, fontFamily: "monospace", wordBreak: "break-all" }}>
          {value}
        </span>
        <button
          onClick={copy}
          style={{
            background: "none", border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 600, color: copied ? COLORS.green : COLORS.accent,
            fontFamily: "inherit", padding: "2px 6px", borderRadius: 4,
            flexShrink: 0,
          }}
        >
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}

export default function SchoolOnboarding({ schoolSlug, schoolName, adminName, onEnterDashboard }) {
  const { session } = useAuth();
  const [step, setStep] = useState(1); // 1 = welcome, 2 = plan, 3 = done
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");

  const firstName = adminName ? adminName.split(" ")[0] : "there";
  const studentUrl = `https://student.careersorter.co/?school=${schoolSlug}`;
  const recruiterUrl = `https://recruiter.careersorter.co/?school=${schoolSlug}`;

  const handlePlanSelect = async (planId) => {
    if (planId === "free") {
      setStep(3);
      return;
    }

    setCheckoutError("");
    setCheckoutLoading(planId);

    try {
      const token = session?.access_token;

      const res = await fetch(`${API}/v1/schools/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          plan: planId,
          success_url: `${window.location.origin}?onboarding_complete=1`,
          cancel_url: `${window.location.origin}?signup_plan=cancelled`,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setCheckoutError(data.detail || "Could not start checkout. Please try again.");
        return;
      }

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch {
      setCheckoutError("Network error. Please try again.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  const card = {
    backgroundColor: COLORS.surface,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    padding: 32,
    maxWidth: 520,
    width: "100%",
  };

  const wrap = {
    fontFamily: "'DM Sans', 'Helvetica Neue', sans-serif",
    backgroundColor: COLORS.bg,
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    flexDirection: "column",
    gap: 16,
  };

  /* ─── STEP 1: Welcome ─── */
  if (step === 1) {
    return (
      <div style={wrap}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
        <div style={card}>
          {/* Checkmark */}
          <div style={{
            width: 48, height: 48, borderRadius: 12, backgroundColor: COLORS.greenLight,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 24, marginBottom: 20,
          }}>✓</div>

          <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 8px" }}>
            {schoolName} is live!
          </h2>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: "0 0 24px" }}>
            Hi {firstName} — your school is set up. Share these links with your team and recruiters.
          </p>

          <CopyField label="Recruiter sign-up link" value={recruiterUrl} />
          <CopyField label="Student portal link" value={studentUrl} />

          <div style={{
            backgroundColor: COLORS.accentLight,
            borderRadius: 8,
            padding: "12px 14px",
            fontSize: 13,
            color: COLORS.accent,
            marginTop: 8,
            marginBottom: 24,
          }}>
            💡 Paste the recruiter link in a LinkedIn post or email to employers. Students will get
            a link when you upload their CVs.
          </div>

          <button
            onClick={() => setStep(2)}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 8, border: "none",
              backgroundColor: COLORS.accent, color: "#fff", fontSize: 15,
              fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
            }}
          >
            Next: Choose a plan →
          </button>

          <button
            onClick={onEnterDashboard}
            style={{
              display: "block", width: "100%", marginTop: 10, padding: "10px 0",
              borderRadius: 8, border: `1px solid ${COLORS.border}`,
              backgroundColor: "transparent", color: COLORS.textSecondary,
              fontSize: 14, fontWeight: 500, fontFamily: "inherit", cursor: "pointer",
            }}
          >
            Skip — go to dashboard
          </button>
        </div>
      </div>
    );
  }

  /* ─── STEP 2: Plan selection ─── */
  if (step === 2) {
    return (
      <div style={{ ...wrap, alignItems: "stretch", maxWidth: 860, margin: "0 auto" }}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />

        <div style={{ textAlign: "center", padding: "20px 0 8px" }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 8px" }}>
            Choose your plan
          </h2>
          <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: 0 }}>
            You can change or cancel at any time.
          </p>
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
          padding: "0 20px 20px",
        }}>
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              style={{
                backgroundColor: COLORS.surface,
                borderRadius: 12,
                border: `2px solid ${plan.highlight ? COLORS.accent : COLORS.border}`,
                padding: 24,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {plan.badge && (
                <div style={{
                  position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)",
                  backgroundColor: COLORS.accent, color: "#fff",
                  fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99,
                  letterSpacing: "0.05em",
                }}>
                  {plan.badge}
                </div>
              )}

              <div style={{ marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.textSecondary }}>
                  {plan.name}
                </span>
              </div>

              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 28, fontWeight: 700, color: COLORS.text }}>
                  {plan.price}
                </span>
                <span style={{ fontSize: 13, color: COLORS.textMuted, marginLeft: 4 }}>
                  {plan.period}
                </span>
              </div>

              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 20px", flex: 1 }}>
                {plan.features.map((f) => (
                  <li key={f} style={{
                    fontSize: 13, color: COLORS.textSecondary,
                    padding: "4px 0", display: "flex", alignItems: "center", gap: 8,
                  }}>
                    <span style={{ color: COLORS.green, fontWeight: 700, flexShrink: 0 }}>✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              {checkoutError && plan.id !== "free" && (
                <p style={{ fontSize: 12, color: COLORS.red, marginBottom: 8 }}>{checkoutError}</p>
              )}

              <button
                onClick={() => handlePlanSelect(plan.id)}
                disabled={checkoutLoading !== null}
                style={{
                  width: "100%",
                  padding: "10px 0",
                  borderRadius: 8,
                  border: plan.ctaStyle === "primary" ? "none" : `1px solid ${COLORS.border}`,
                  backgroundColor: plan.ctaStyle === "primary"
                    ? COLORS.accent
                    : "transparent",
                  color: plan.ctaStyle === "primary" ? "#fff" : COLORS.text,
                  fontSize: 14,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  cursor: checkoutLoading !== null ? "not-allowed" : "pointer",
                  opacity: checkoutLoading && checkoutLoading !== plan.id ? 0.5 : 1,
                }}
              >
                {checkoutLoading === plan.id ? "Redirecting…" : plan.cta}
              </button>
            </div>
          ))}
        </div>

        <p style={{ textAlign: "center", fontSize: 12, color: COLORS.textMuted, paddingBottom: 20 }}>
          Need a custom contract or 500+ student seats?{" "}
          <a href="mailto:bernt@careersorter.co" style={{ color: COLORS.accent }}>
            Contact us
          </a>
        </p>
      </div>
    );
  }

  /* ─── STEP 3: Done ─── */
  return (
    <div style={wrap}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <div style={{ ...card, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: "0 0 8px" }}>
          You're all set
        </h2>
        <p style={{ fontSize: 14, color: COLORS.textSecondary, margin: "0 0 24px" }}>
          {schoolName} is ready to go. Upload some CVs, share the recruiter link, and you're live.
        </p>

        <button
          onClick={onEnterDashboard}
          style={{
            width: "100%", padding: "12px 0", borderRadius: 8, border: "none",
            backgroundColor: COLORS.accent, color: "#fff", fontSize: 15,
            fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
          }}
        >
          Open dashboard →
        </button>
      </div>
    </div>
  );
}
